import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { AutoComplete } from 'antd';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { withRouter } from 'utils/withRouter';
import { Input } from 'ui';
import { v4 as uuidv4 } from 'uuid';
import debounce from 'lodash.debounce';

const StyledAutoComplete = styled(AutoComplete)`
  font-size: 16px;
  font-weight: 400;
  .ant-select-selection-search-input {
    height: 32px;
  }
  input {
    height: auto !important;
  }
`;

const UsersDropdown = ({
  users = {},
  fetchUsers,
  fetchRestrictedUsers,
  loading,
  intl,
  onSelect,
  disabled,
  userDisplayName,
  onSearch,
  placeholder = intl.formatMessage({ id: 'ticket.RECIPIENT_LABEL' }),
  clearUsersData,
  clearUsersDataCb,
  onChange,
  removeDuplicatesFrom = [],
  isRestricted,
  entityScope,
  ...rest
}) => {
  const [dropdownId] = useState(uuidv4());

  const debouncedRestrictedSearch = useCallback(
    debounce(options => fetchRestrictedUsers(options), 300),
    [],
  );

  const debouncedSearch = useCallback(
    debounce(options => fetchUsers(options), 300),
    [],
  );

  useEffect(() => {
    if (clearUsersDataCb) {
      clearUsersDataCb(() => clearUsersData({ dropdownId }));
    }
    return () => {
      clearUsersData({ dropdownId });
    };
  }, []);

  const getUsersOptions = () =>
    // We get back all users (as object), we only select what's relevant for this dropdown.

    users[dropdownId]?.length &&
    users[dropdownId]
      ?.filter(u => !removeDuplicatesFrom?.find(d => d.id === u.objectId))
      ?.map(r => ({
        value: r.objectId,
        label: `${r.displayName} ${r.mail ? `(${r.mail})` : ''}`,
      }),
      );
  const handleSelect = (v) => {
    const selectedUser = users[dropdownId]?.find(i => i.objectId === v);
    onSelect({ value: v, selectedUser });
  };

  const handleOnChange = (v) => {
    if (typeof onChange !== 'undefined') {
      onChange(v);
    }
  };

  const handleSearch = (v) => {
    if (v?.length > 2) {
      if (isRestricted) {
        debouncedRestrictedSearch({ searchString: v, dropdownId, entityScope });
      } else {
        debouncedSearch({ searchString: v, dropdownId });
      }
    } else {
      clearUsersData({ dropdownId });
    }
    if (onSearch) {
      onSearch(v);
    }
  };

  const options = getUsersOptions();
  return (
    <StyledAutoComplete
      options={options?.length ? options : []}
      defaultValue={userDisplayName}
      onSelect={handleSelect}
      onSearch={handleSearch}
      onChange={handleOnChange}
      disabled={disabled}
      {...rest}
    >
      <Input
        suffix={loading && loading[dropdownId] ? <LoadingOutlined /> : <span />}
        placeholder={placeholder}
      />
    </StyledAutoComplete>
  );
};

UsersDropdown.propTypes = {
  users: PropTypes.object,
  fetchUsers: PropTypes.func.isRequired,
  fetchRestrictedUsers: PropTypes.func.isRequired,
  loading: PropTypes.object,
  intl: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  userDisplayName: PropTypes.string,
  value: PropTypes.string,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  clearUsersData: PropTypes.func,
  clearUsersDataCb: PropTypes.func,
  onChange: PropTypes.func,
  removeDuplicatesFrom: PropTypes.array,
  isRestricted: PropTypes.bool,
  entityScope: PropTypes.string,
};

export default injectIntl(withRouter(UsersDropdown));
